import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Building2, Bell, FileText, Settings, LineChart, MessageSquare } from "lucide-react";
import Layout from "../components/Layout";
import { HeroGlow } from "../components/ui/hero";
import { StatCard } from "../components/ui/stat-card";
import { DataTable, DataTableColumn } from "../components/ui/data-table";
import { SectionLabel } from "../components/ui/section-label";
import { Button } from "../components/ui/button";
import { UserAvatar } from "../components/ui/user-avatar";
import { StatusBadge, PriorityBadge } from "../components/VacatureBadges";
import { useAuth } from "../hooks/useAuth";
import { useKamDashboard, useHmpDashboard, useVacatures, usePartnerAdminDashboard, useProfile, usePartners, useTasks } from "../hooks/queries";
import { useChatWidget } from "../components/ChatWidget";
import { useSettingsDialog } from "../components/SettingsDialog";
import { WavesLogo } from "../components/WavesLogo";
import { listVacaturesByIds } from "../api/api";
import type { Vacature, KamDashboard, Partner } from "../api/api";
import { vacatureToSwipeCard } from "../lib/swipe";
import { partnerIconUrl } from "../lib/partner";
import { cn } from "@/lib/utils";

const DAY_MS = 86_400_000;

// ── Helpers ────────────────────────────────────────────────────

function greeting(): string {
  const h = new Date().getHours();
  if (h < 12) return "Goedemorgen";
  if (h < 18) return "Goedemiddag";
  return "Goedenavond";
}

function todayLabel(): string {
  const s = new Date().toLocaleDateString("nl-NL", { weekday: "long", day: "numeric", month: "long" });
  return s.charAt(0).toUpperCase() + s.slice(1);
}

function timeAgo(iso: string): string {
  const mins = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
  if (mins < 1) return "nu";
  if (mins < 60) return `${mins}m`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}u`;
  return `${Math.floor(hrs / 24)}d`;
}

function shortDate(iso: string | null | undefined): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("nl-NL", { day: "numeric", month: "short" });
}

function daysOpen(v: Vacature): number {
  return Math.floor((Date.now() - new Date(v.created_at).getTime()) / DAY_MS);
}

function actionLabel(action: string, model: string): string {
  const m = model === "job_openings" ? "vacature" : model.replace(/_/g, " ");
  if (action === "create") return `voegde een ${m} toe`;
  if (action === "update") return `wijzigde een ${m}`;
  if (action === "delete") return `verwijderde een ${m}`;
  if (action === "confirmed") return "bevestigde een vacature als actueel";
  if (action === "status_change") return "wijzigde de status van een vacature";
  if (action === "bemiddeling_change") return "wijzigde de bemiddeling van een vacature";
  return action;
}

const AVATAR_TONES = ["blue", "green", "amber", "stone"] as const;

// ── Shared dashboard building blocks ───────────────────────────

/** The design's dashboard backdrop: white fading into a light-blue band behind the hero. */
function DashboardShell({ children }: { children: React.ReactNode }) {
  return (
    <div
      className="min-h-full"
      style={{ background: "linear-gradient(180deg, #FFFFFF 0px, #FBFDFE 110px, #EEF5FB 230px, #DCEBF6 312px, #FFFFFF 312px)" }}
    >
      <div className="mx-auto flex w-full max-w-[1240px] flex-col gap-6 px-4 pb-11 pt-20 md:px-14 md:pt-14">
        {children}
      </div>
    </div>
  );
}

function DashboardHero({ logo, name, intro }: { logo: React.ReactNode; name: string; intro: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-4 md:pr-28">
      <div className="flex items-center gap-4">
        {logo}
        <div className="h-[18px] w-px bg-waves-line" />
        <SectionLabel>{todayLabel()}</SectionLabel>
      </div>
      <h1 className="text-[34px] font-black leading-[1.06] tracking-[-0.025em] md:text-[44px]">
        {greeting()} <span className="text-waves-accent">{name}</span>.
      </h1>
      <p className="max-w-2xl text-[15px] leading-[1.65] text-waves-body">{intro}</p>
    </div>
  );
}

function PartnerLogo({ partner }: { partner?: Partner }) {
  if (partner?.logo_url) return <img src={partner.logo_url} alt={partner.name} className="block h-[34px] w-auto" />;
  if (partner) return <span className="text-[15px] font-black tracking-[-0.02em]">{partner.name}</span>;
  return <WavesLogo className="h-[26px]" />;
}

/** The big white call-to-action card under the hero (the check / taken prompt). */
function CtaCard({ title, sub, aside, action, tour }: { title: React.ReactNode; sub?: React.ReactNode; aside?: React.ReactNode; action?: React.ReactNode; tour?: string }) {
  return (
    <div data-tour={tour} className="relative mt-5 overflow-hidden rounded-card-lg border border-waves-line bg-white transition-[transform,box-shadow] duration-150 hover:-translate-y-1 hover:shadow-lift">
      <div className="relative z-[1] flex flex-col gap-5 p-6 md:flex-row md:items-center md:gap-6 md:p-9">
        <div className="flex flex-1 flex-col gap-2">
          <div className="text-2xl font-black leading-[1.1] tracking-[-0.02em] md:text-[30px]">{title}</div>
          {sub && <div className="pt-1 text-[15px] text-waves-body">{sub}</div>}
        </div>
        {(aside || action) && (
          <div className="flex shrink-0 items-center gap-4">
            {aside && <div className="font-mono text-[13px] text-waves-muted">{aside}</div>}
            {action}
          </div>
        )}
      </div>
    </div>
  );
}

function Accent({ children }: { children: React.ReactNode }) {
  return <span className="text-waves-accent">{children}</span>;
}

// ── Hiring manager dashboard ───────────────────────────────────

type HmpFilter = "open" | "hoog" | "ouder30" | "gesloten" | null;

function HmpLanding() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { openAlgemeenChat } = useChatWidget();
  const { data: profile } = useProfile();
  const { data: partners = [] } = usePartners();
  const partnerId = user?.partner_id ?? 0;
  const partner = partners.find(p => p.id === partnerId);
  const firstName = (user?.name ?? "").split(" ")[0];
  const { data: hmpData, isLoading: hmpLoading } = useHmpDashboard(partnerId);
  const { data: vacResponse } = useVacatures({ partner_id: partnerId, limit: 500 });
  const vacatures = useMemo(() => vacResponse?.data ?? [], [vacResponse]);
  // The table only lists vacatures this user created or changed; the stat
  // cards and check prompt above it still cover the whole partner.
  const { data: mineResponse, isLoading: mineLoading } = useVacatures({ partner_id: partnerId, mine: true, limit: 500 });
  const myVacatures = useMemo(() => mineResponse?.data ?? [], [mineResponse]);
  const [filter, setFilter] = useState<HmpFilter>(null);

  // The review batches and their day counts come from the HMP's review
  // settings (backend service.reviewBatches); the extra batch is offered at
  // the end of the check, or is the check itself when the main one is empty.
  const stale = hmpData?.stale_vacatures ?? [];
  const extraIds = hmpData?.secondary_vacature_ids ?? [];
  const [startingExtra, setStartingExtra] = useState(false);

  const counts = useMemo(() => ({
    open:     vacatures.filter(v => v.status === "open").length,
    hoog:     vacatures.filter(v => v.priority === "hoog" && v.status !== "gesloten").length,
    ouder30:  vacatures.filter(v => v.status !== "gesloten" && daysOpen(v) > 30).length,
    gesloten: vacatures.filter(v => v.status === "gesloten").length,
    active:   vacatures.filter(v => v.status !== "gesloten").length,
  }), [vacatures]);

  const rows = useMemo(() => {
    switch (filter) {
      case "open":     return myVacatures.filter(v => v.status === "open");
      case "hoog":     return myVacatures.filter(v => v.priority === "hoog" && v.status !== "gesloten");
      case "ouder30":  return myVacatures.filter(v => v.status !== "gesloten" && daysOpen(v) > 30);
      case "gesloten": return myVacatures.filter(v => v.status === "gesloten");
      default:         return myVacatures.filter(v => v.status !== "gesloten");
    }
  }, [myVacatures, filter]);

  const toggle = (f: Exclude<HmpFilter, null>) => setFilter(cur => (cur === f ? null : f));

  const startExtraCheck = async () => {
    setStartingExtra(true);
    try {
      const { data } = await listVacaturesByIds(extraIds);
      navigate("/swipe-review", { state: { cards: data.map(vacatureToSwipeCard), title: "Extra vacatures" } });
    } catch { /* handled by the global error snackbar */ }
    finally { setStartingExtra(false); }
  };

  const columns: DataTableColumn<Vacature>[] = [
    { key: "functie", header: "Functietitel", width: "minmax(200px,1fr)", sortValue: v => v.functie, cell: v => <span className="font-bold">{v.functie}</span> },
    { key: "status", header: "Status", width: "110px", cell: v => <StatusBadge value={v.status} /> },
    { key: "prio", header: "Prioriteit", width: "110px", cell: v => <PriorityBadge value={v.priority} /> },
    { key: "days", header: "Dagen open", width: "120px", sortValue: daysOpen, cell: v => <span className="font-mono text-sm font-semibold">{daysOpen(v)}</span> },
    { key: "changed", header: "Gewijzigd", width: "110px", sortValue: v => v.updated_at ?? v.created_at, cell: v => <span className="font-mono text-sm text-waves-body">{shortDate(v.updated_at ?? v.created_at)}</span> },
  ];

  const contacts = profile?.waves_contacts ?? [];
  const contactName = contacts[0]?.name ?? "Waves Network";
  const contactNames = contacts.length > 0 ? contacts.map(c => c.name).join(" & ") : contactName;

  return (
    <Layout bare>
      <DashboardShell>
        <DashboardHero
          logo={<PartnerLogo partner={partner} />}
          name={firstName}
          intro="Hier zie je in één oogopslag hoe je vacatures ervoor staan. Houd ze actueel met de vacaturecheck."
        />

        {hmpLoading ? (
          <div className="mt-5 h-32 animate-pulse rounded-card-lg border border-waves-line bg-white" />
        ) : stale.length > 0 ? (
          <CtaCard
            tour="dash-check"
            title={<><Accent>{stale.length} {stale.length === 1 ? "vacature is" : "vacatures zijn"}</Accent> al <Accent>{hmpData!.review_days} dagen</Accent> niet bijgewerkt</>}
            sub={extraIds.length > 0 && `Daarna ${extraIds.length === 1 ? "nog 1 vacature" : `nog ${extraIds.length} vacatures`} die je ook kunt bijwerken`}
            aside={`± ${Math.max(1, Math.round((stale.length * 20) / 60))} min`}
            action={<Button size="lg" data-tour="start-check" onClick={() => navigate("/swipe-review")}>Start de check</Button>}
          />
        ) : extraIds.length > 0 ? (
          <CtaCard
            tour="dash-check"
            title={<>Al je vacatures zijn <Accent>actueel</Accent></>}
            sub={`Wil je toch reviewen? Er ${extraIds.length === 1 ? "is 1 vacature die" : `zijn ${extraIds.length} vacatures die`} ${hmpData!.secondary_review_days} dagen niet ${extraIds.length === 1 ? "is" : "zijn"} bijgewerkt.`}
            aside={`± ${Math.max(1, Math.round((extraIds.length * 20) / 60))} min`}
            action={<Button size="lg" data-tour="start-check" disabled={startingExtra} onClick={startExtraCheck}>{startingExtra ? "Laden…" : "Start de check"}</Button>}
          />
        ) : (
          <CtaCard
            tour="dash-check"
            title={<>Al je vacatures zijn <Accent>actueel</Accent></>}
            sub="Mooi zo — er staat geen vacaturecheck voor je klaar."
          />
        )}

        <div data-tour="dash-tiles" className="grid grid-cols-[repeat(auto-fill,minmax(220px,1fr))] gap-4">
          <StatCard title="Openstaande vacatures" value={counts.open} caption="vacatures open" hint="Bekijk open vacatures →" active={filter === "open"} onClick={() => toggle("open")} />
          <StatCard title="Vacatures met hoge prioriteit" value={counts.hoog} caption="vacatures met urgentie" hint="Bekijk urgente vacatures →" active={filter === "hoog"} onClick={() => toggle("hoog")} />
          <StatCard title="Vacatures ouder dan 30 dagen" value={counts.ouder30} caption={`van ${counts.active} vacatures`} hint="Bekijk oudere vacatures →" active={filter === "ouder30"} onClick={() => toggle("ouder30")} />
          <StatCard title="Gesloten vacatures" value={counts.gesloten} caption="vacatures gesloten" hint="Bekijk gesloten vacatures →" active={filter === "gesloten"} onClick={() => toggle("gesloten")} />
        </div>

        <div className="flex flex-col gap-6 xl:flex-row xl:items-start">
          <div className="min-w-0 flex-1">
            <DataTable
              columns={columns}
              rows={rows}
              rowKey={v => v.id}
              onRowClick={v => navigate(`/vacature/${v.id}`)}
              minWidth={660}
              tour="dash-table"
              empty={mineLoading ? "Vacatures laden…" : filter ? "Geen vacatures gevonden — pas het filter aan." : "Je hebt nog geen vacatures aangemaakt of gewijzigd."}
            />
          </div>

          <div className="flex w-full shrink-0 flex-col overflow-hidden rounded-card border border-waves-line bg-white xl:w-[340px]">
            <div className="flex items-center gap-4 border-b border-waves-line p-6">
              <div className="relative shrink-0">
                <UserAvatar name={contactName} size={56} />
                <span className="absolute bottom-px right-px h-[13px] w-[13px] rounded-full border-2 border-white bg-status-ok-fg" />
              </div>
              <div className="flex min-w-0 flex-col gap-0.5">
                <SectionLabel>Chat</SectionLabel>
                <div className="truncate text-[17px] font-bold">{contactNames}</div>
                <div className="truncate text-[13px] text-waves-muted">{contacts.length > 1 ? "jouw contactpersonen" : "jouw contactpersoon"}</div>
              </div>
            </div>
            <div className="flex flex-1 flex-col gap-3 bg-waves-subtle px-6 py-5">
              <div className="flex items-end gap-2">
                <UserAvatar name={contactName} size={24} />
                <div className="rounded-[16px_16px_16px_4px] bg-waves-mist px-3.5 py-2.5 text-sm leading-normal">
                  Hoi {firstName}! Waar kan ik je mee helpen?
                </div>
              </div>
            </div>
            <div className="flex gap-2 border-t border-waves-line px-6 pb-6 pt-4">
              <button
                onClick={() => openAlgemeenChat()}
                className="flex-1 rounded-pill border border-waves-line bg-white px-[18px] py-[13px] text-left text-sm text-waves-muted transition-colors hover:bg-waves-mist"
              >
                Typ een bericht…
              </button>
              <Button variant="ink" onClick={() => openAlgemeenChat()}>Chat</Button>
            </div>
          </div>
        </div>
      </DashboardShell>
    </Layout>
  );
}

// ── Key account manager / admin dashboard ──────────────────────

type PartnerRow = KamDashboard["per_partner"][number];

function KamDashboardPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const { data, isLoading } = useKamDashboard(isAdmin);
  const { data: partners = [] } = usePartners();
  const { data: tasksData } = useTasks();
  const tasks = tasksData?.tasks ?? [];
  const firstName = (user?.name ?? "").split(" ")[0];
  const partnerById = useMemo(() => new Map(partners.map(p => [p.id, p])), [partners]);

  const partnerColumns: DataTableColumn<PartnerRow>[] = [
    {
      key: "partner", header: "Partner", width: "minmax(160px,1fr)", sortValue: p => p.partner_name,
      cell: p => {
        const partner = partnerById.get(p.partner_id);
        const iconUrl = partner ? partnerIconUrl(partner) : null;
        return (
          <div className="flex items-center gap-3">
            {iconUrl
              ? <img src={iconUrl} alt="" className="h-7 w-7 object-contain" />
              : <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-waves-lucht"><Building2 size={14} /></span>}
            <span className="truncate font-bold">{p.partner_name}</span>
          </div>
        );
      },
    },
    { key: "open", header: "Open", width: "64px", sortValue: p => p.open, cell: p => <span className="font-mono text-sm font-semibold">{p.open}</span> },
    { key: "hoog", header: "Hoog", width: "64px", sortValue: p => p.hoog, cell: p => <span className={cn("font-mono text-sm font-semibold", p.hoog > 0 ? "text-status-alert-fg" : "text-waves-muted")}>{p.hoog}</span> },
    { key: "bemiddeling", header: "Bemid.", width: "72px", sortValue: p => p.in_bemiddeling, cell: p => <span className="font-mono text-sm text-waves-body">{p.in_bemiddeling}</span> },
    { key: "dicht", header: "Dicht", width: "64px", sortValue: p => p.gesloten, cell: p => <span className="font-mono text-sm text-waves-body">{p.gesloten}</span> },
  ];

  const nPartners = data?.per_partner.length ?? 0;

  return (
    <Layout bare>
      <DashboardShell>
        <DashboardHero
          logo={<WavesLogo className="h-[26px]" />}
          name={firstName}
          intro={isLoading ? "Even je overzicht ophalen…" : `Zo staat het ervoor bij je ${nPartners === 1 ? "partner" : `${nPartners} partners`}.`}
        />

        <CtaCard
          title={tasks.length > 0
            ? <><Accent>{tasks.length} {tasks.length === 1 ? "taak wacht" : "taken wachten"}</Accent> op je</>
            : <>Alles is <Accent>afgehandeld</Accent></>}
          sub={tasks.length > 0 ? tasks.map(t => t.title).join(" · ") : "Er staan geen taken voor je open."}
          action={tasks.length > 0 && <Button size="lg" onClick={() => navigate("/taken")}>Naar taken</Button>}
        />

        <div className="grid grid-cols-[repeat(auto-fit,minmax(210px,1fr))] gap-4">
          <StatCard title="Openstaande vacatures" value={isLoading ? "…" : data?.open_vacatures ?? 0} caption={`van ${data?.totaal_vacatures ?? 0} vacatures`} hint="Alle partners →" onClick={() => navigate("/vacatures")} />
          <StatCard title="Hoge prioriteit" value={isLoading ? "…" : data?.hoge_prioriteit ?? 0} caption="open met urgentie" hint="Alle partners →" onClick={() => navigate("/vacatures")} />
          <StatCard title="In bemiddeling" value={isLoading ? "…" : data?.in_bemiddeling ?? 0} caption="kandidaten voorgesteld" hint="Alle partners →" onClick={() => navigate("/vacatures")} />
          <StatCard title="Geplaatst" value={isLoading ? "…" : data?.geplaatst ?? 0} caption="vacatures ingevuld" hint="Alle partners →" onClick={() => navigate("/vacatures")} />
        </div>

        <div className="flex flex-col gap-6 xl:flex-row xl:items-start">
          <div className="min-w-0 flex-1">
            <div className="flex items-center justify-between gap-4 px-1 pb-3">
              <div className="text-base font-bold">Partners</div>
              <button onClick={() => navigate("/vacatures")} className="text-[13px] font-bold hover:text-waves-accent">Alle partners →</button>
            </div>
            <DataTable
              columns={partnerColumns}
              rows={data?.per_partner ?? []}
              rowKey={p => p.partner_id}
              onRowClick={p => navigate(`/vacatures/${p.partner_id}`)}
              minWidth={520}
              empty={isLoading ? "Laden…" : "Nog geen partners."}
            />
          </div>

          <div className="flex w-full shrink-0 flex-col gap-1 rounded-card border border-waves-line bg-white px-6 pb-6 pt-5 xl:w-[380px]">
            <div className="pb-2 text-base font-bold">Recente activiteit</div>
            {(data?.recent_activity ?? []).slice(0, 8).map((a, i) => (
              <div key={a.id} className="flex items-center gap-3 border-t border-status-neutral-bg py-2.5">
                <UserAvatar name={a.actor_email} size={26} tone={AVATAR_TONES[i % 4]} />
                <div className="min-w-0 flex-1 text-sm leading-normal text-waves-body">
                  <b className="text-waves-ink">{a.actor_email.split("@")[0]}</b> {actionLabel(a.action, a.model_name)}
                </div>
                <div className="shrink-0 font-mono text-xs text-waves-muted">{timeAgo(a.created_at)}</div>
              </div>
            ))}
            {!isLoading && (data?.recent_activity ?? []).length === 0 && (
              <p className="border-t border-status-neutral-bg py-3 text-sm text-waves-muted">Nog geen activiteit.</p>
            )}
            <Button variant="outline" className="mt-3" onClick={() => navigate("/audit-trail")}>Naar audit trail →</Button>
          </div>
        </div>
      </DashboardShell>
    </Layout>
  );
}

// ── Partner admin dashboard (derived from the HM design) ───────

function PartnerAdminLanding() {
  const navigate = useNavigate();
  const { openSettings } = useSettingsDialog();
  const { user } = useAuth();
  const { data: partners = [] } = usePartners();
  const partner = partners.find(p => p.id === user?.partner_id);
  const firstName = (user?.name ?? "").split(" ")[0];
  const { data, isLoading } = usePartnerAdminDashboard();

  const tiles = [
    { icon: Building2, title: "Vestigingen", desc: "Vacatures per vestiging beheren", open: () => navigate(`/vacatures/${user?.partner_id ?? ""}`) },
    { icon: LineChart, title: "Marktstatistieken", desc: "Het Waves-netwerk per regio", open: () => navigate("/marktstatistieken") },
    { icon: MessageSquare, title: "Werkgeversinzichten", desc: "Signalen en berichten uit de markt", open: () => navigate("/werkgeversinzichten") },
    { icon: Bell, title: "Meldingen", desc: "Recente acties en updates", open: () => navigate("/meldingen") },
    { icon: FileText, title: "Audit trail", desc: "Overzicht van alle wijzigingen", open: () => navigate("/audit-trail") },
    { icon: Settings, title: "Instellingen", desc: "Beheer je account en voorkeuren", open: () => openSettings() },
  ];

  const v = (n: number | undefined) => (isLoading ? "…" : n ?? 0);

  return (
    <Layout bare>
      <DashboardShell>
        <DashboardHero
          logo={<PartnerLogo partner={partner} />}
          name={firstName}
          intro={`Zo staat de samenwerking tussen ${data?.partner_name ?? partner?.name ?? "jullie"} en Waves ervoor.`}
        />

        <div className="mt-5 grid grid-cols-[repeat(auto-fill,minmax(220px,1fr))] gap-4">
          <StatCard title="Openstaande vacatures" value={v(data?.open_vacatures)} caption={`van ${data?.totaal_vacatures ?? 0} vacatures`} hint="Bekijk vacatures →" onClick={() => navigate(`/vacatures/${user?.partner_id ?? ""}`)} />
          <StatCard title="Vacatures met hoge prioriteit" value={v(data?.hoge_prioriteit)} caption="vacatures met urgentie" hint="Bekijk vacatures →" onClick={() => navigate(`/vacatures/${user?.partner_id ?? ""}`)} />
          <StatCard title="In bemiddeling" value={v(data?.in_bemiddeling)} caption="kandidaten voorgesteld" />
          <StatCard title="Geplaatst" value={v(data?.geplaatst)} caption="vacatures ingevuld" />
        </div>

        <div className="flex flex-col gap-4">
          <div className="text-xl font-black tracking-[-0.02em]">Waar ben je naar op zoek, <Accent>{firstName}</Accent>?</div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
            {tiles.map(t => (
              <button
                key={t.title}
                onClick={t.open}
                className="group flex items-start gap-4 rounded-card border border-waves-line bg-white p-6 text-left transition-[transform,box-shadow] duration-150 hover:-translate-y-1 hover:shadow-lift"
              >
                <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-nav bg-waves-mist">
                  <t.icon size={19} />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-base font-bold">{t.title}</div>
                  <div className="mt-1 text-sm text-waves-muted">{t.desc}</div>
                </div>
                <span className="text-waves-muted transition-colors group-hover:text-waves-ink">→</span>
              </button>
            ))}
          </div>
        </div>
      </DashboardShell>
    </Layout>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  if (user?.role === "partner_admin") return <PartnerAdminLanding />;
  if (user?.role === "hmp") return <HmpLanding />;
  return <KamDashboardPage />;
}
